/**
 * @owner @Appboy/intelligence
 *
 * Derives the trackable email links for a Content Optimization step — the same links the marketer
 * can pick as conversion links in the optimization settings. Shared by the settings composer and by
 * click-no-link validation so both agree on what counts as a link.
 */

import type { ContentOptimizationEmailLink } from "./types";

const emptyEmailLinks: ContentOptimizationEmailLink[] = [];

const getLidFromHref = (href: string): string | null => {
  try {
    return new URL(href, window.location.origin).searchParams.get("lid");
  } catch {
    return href.match(/[?&]lid=([^&#]+)/)?.[1] ?? null;
  }
};

// Parse the email body for tracked anchors (those carrying a `lid`). Used so we don't require the
// marketer to save the step before the link aliases are loaded onto the message.
export const getEmailLinksFromBody = (body?: string | null): ContentOptimizationEmailLink[] => {
  if (!body) {
    return emptyEmailLinks;
  }

  const document = new DOMParser().parseFromString(body, "text/html");
  return Array.from(document.querySelectorAll("a[href]")).flatMap((anchor) => {
    const href = anchor.getAttribute("href") ?? "";
    const lid = getLidFromHref(href);
    if (!lid) {
      return [];
    }

    const label = anchor.textContent?.trim();
    return [
      {
        active: true,
        is_tracked: true,
        lid,
        name: label || href,
        url: href,
      },
    ];
  });
};

// Prefer active, tracked link aliases when present; otherwise fall back to parsing the body.
export const getEmailLinks = (
  linkAliasLinks?: ContentOptimizationEmailLink[] | null,
  body?: string | null
): ContentOptimizationEmailLink[] => {
  const trackedLinkAliasLinks = linkAliasLinks?.filter((link) => link.active && link.is_tracked && link.lid) ?? [];
  if (trackedLinkAliasLinks.length > 0) {
    return trackedLinkAliasLinks;
  }

  return getEmailLinksFromBody(body);
};
